import type { CryptoCurrency } from '@0xtorch/core'
import { upsertCryptoCurrencies } from '@pkg/database-portfolio'
import type { RunToDatabase } from '../types'

type FetchMissingCryptoCurrenciesParams = {
  /** symbolAssetMap から抽出された crypto ID のセット */
  requiredCryptoIds: Set<string>
  /** ローカルDBの crypto currency リスト */
  existingCryptoes: readonly CryptoCurrency[]
  /** crypto-currency API エンドポイント */
  cryptoCurrencyApiEndpoint: string
  /** DB 書き込み関数 */
  writeToDatabase: RunToDatabase
}

/**
 * symbolAssetMap に含まれているがローカルDBにない crypto currency を
 * API から取得してローカルDBに保存する
 */
export const fetchMissingCryptoCurrencies = async ({
  requiredCryptoIds,
  existingCryptoes,
  cryptoCurrencyApiEndpoint,
  writeToDatabase,
}: FetchMissingCryptoCurrenciesParams): Promise<readonly CryptoCurrency[]> => {
  // 既存の crypto ID セット
  const existingIds = new Set(existingCryptoes.map((c) => c.id))

  // 不足している ID を特定
  const missingIds = [...requiredCryptoIds].filter((id) => !existingIds.has(id))

  if (missingIds.length === 0) {
    return existingCryptoes
  }

  // API から不足トークンを取得
  const fetchedCryptoes = await fetchCryptoCurrenciesFromApi({
    cryptoCurrencyApiEndpoint,
    ids: missingIds,
  })

  // 期待したが取得できなかったトークンを警告（Sentry で確認可能）
  const fetchedIds = new Set(fetchedCryptoes.map((c) => c.id))
  const notFoundIds = missingIds.filter((id) => !fetchedIds.has(id))
  if (notFoundIds.length > 0) {
    console.warn(
      `[fetchMissingCryptoCurrencies] Crypto currencies not found in API (need to register): ${notFoundIds.join(', ')}`,
    )
  }

  if (fetchedCryptoes.length === 0) {
    return existingCryptoes
  }

  // ローカルDBに保存
  await writeToDatabase((database) =>
    upsertCryptoCurrencies({
      database,
      data: fetchedCryptoes.map((currency) => ({
        id: currency.id,
        name: currency.name,
        symbol: currency.symbol,
        icon: currency.icon ?? null,
        coingeckoId: currency.market?.coingeckoId ?? null,
        marketCapUsd: currency.market?.marketCapUsd ?? null,
        priceDatasourceId: currency.priceDatasourceId ?? null,
        updatedAt: currency.updatedAt,
      })),
    }),
  )

  // 更新された crypto currency リストを返す
  return [...existingCryptoes, ...fetchedCryptoes]
}

/**
 * crypto-currency API から指定された ID のトークンを取得
 */
const fetchCryptoCurrenciesFromApi = async ({
  cryptoCurrencyApiEndpoint,
  ids,
}: {
  cryptoCurrencyApiEndpoint: string
  ids: string[]
}): Promise<CryptoCurrency[]> => {
  if (ids.length === 0) {
    return []
  }

  try {
    // ID をクエリパラメータとして渡す（最大50件ずつ）
    const results: CryptoCurrency[] = []
    const chunkSize = 50

    for (let i = 0; i < ids.length; i += chunkSize) {
      const chunk = ids.slice(i, i + chunkSize)
      const params = new URLSearchParams()
      for (const id of chunk) {
        params.append('id', id)
      }
      // Add cache-busting timestamp to ensure we get the latest data
      params.append('_t', Date.now().toString())

      const response = await fetch(
        `${cryptoCurrencyApiEndpoint}/v1/list?${params.toString()}`,
      )

      if (!response.ok) {
        console.error(
          `Failed to fetch crypto currencies: ${response.status} ${response.statusText}`,
        )
        continue
      }

      const data = (await response.json()) as CryptoCurrency[]
      results.push(...data)
    }

    return results
  } catch (error) {
    console.error('Failed to fetch missing crypto currencies:', error)
    return []
  }
}

/**
 * symbolAssetMap から crypto ID を抽出する
 */
export const extractCryptoIdsFromSymbolAssetMap = (
  symbolAssetMap: Record<string, string>,
): Set<string> => {
  const cryptoIds = new Set<string>()

  for (const assetKey of Object.values(symbolAssetMap)) {
    if (assetKey.startsWith('crypto/')) {
      const id = assetKey.slice('crypto/'.length)
      cryptoIds.add(id)
    }
  }

  return cryptoIds
}
